import dayjs from 'dayjs';

import {config} from 'config/env';
import ReactNative from 'services/react-native';
import {AffiliateProgramType} from 'store/types/app';
import {
  Address,
  AsAzureRoles,
  Basket,
  BasketItem,
  BasketLineItem,
  BasketRoot,
  BasketState,
  CheckoutTypeEnum,
  CustomAttributeKey,
  CustomAttributeSource,
  DropShipDeliverySlot,
  ISellerFromBasketItems,
  MarketplaceShopType,
  Product,
  ProductSearchResult,
  ProductStatusType,
  ShippingInfo,
  TGetBasketPromtionsLitePayload,
  Variant
} from 'types';
import {ProductUnavailableReasonCodes} from 'types/recommedation-types/recommendations';

import {PROMOTION_LITE_IS_DROPSHIPMENT, PROMOTION_LITE_UNIT_DISCOUNT} from '../constants';
import {getProductAvailability, getProductIdWithoutPrefix} from './product';

const VARIANT_ID_PREFIX = 'gid://shopify/ProductVariant/';

const formatPreOrderDeliveryDate = (date: string | Date): string => {
  const dateOnly = typeof date === 'string' ? date.split('T')[0] : dayjs(date).format('YYYY-MM-DD');

  return dayjs(dateOnly).toString();
};

export const getPreOrderDeliverySlot = (shippingInfo?: ShippingInfo, sellerName?: string): DropShipDeliverySlot => {
  return {
    sellerName: sellerName ? sellerName : '',
    deliveryTimeEarliestDate: formatPreOrderDeliveryDate(
      shippingInfo?.deliveryTimeEarliestDate || config.PRE_ORDER_START_DATE
    ),
    deliveryTimeLatestDate: formatPreOrderDeliveryDate(
      shippingInfo?.deliveryTimeLatestDate || config.PRE_ORDER_END_DATE
    )
  };
};

export const filterUnavailableProducts = (lineItems: BasketLineItem[], storeCodes: string[]): BasketLineItem[] => {
  return lineItems.filter((i) => !getProductAvailability(i.productDetails, storeCodes).isAvailable);
};

export const filterUnavailableProductsForKAMCustomers = (
  lineItems: BasketLineItem[],
  storeCodes: string[]
): BasketLineItem[] => {
  return lineItems.filter((i) => !getProductAvailability(i.productDetails, storeCodes).isOutOfStock);
};

export const getBulkyDeliverySlot = (sellerName?: string): DropShipDeliverySlot => {
  return {
    sellerName: sellerName ? sellerName : '',
    deliveryTimeEarliestDate: dayjs().add(3, 'days').toString(),
    deliveryTimeLatestDate: dayjs().add(7, 'days').toString()
  };
};

export const getProductShopName = (product: Product): string => {
  return (product?.sellerDetails?.marketplaceType === MarketplaceShopType.CONSIGNMENT ||
    product?.sellerDetails?.marketplaceType === null) &&
    product?.sellerDetails?.fulfilledByPlatform
    ? product?.sellerDetails?.fulfilledByPlatform
    : product?.sellerDetails?.shopName;
};

export const get1PxProducts = (availableItems: BasketLineItem[]) => {
  if (!availableItems.length) return [];
  return availableItems.filter((product) => Boolean(product?.productDetails?.is1PX) === true);
};

export const areAll1PxNonBulkyProducts = (items: BasketLineItem[]): boolean => {
  if (!items?.length) return false;

  return items.every((item) => item?.productDetails?.is1PX === true && item?.productDetails?.isBulky !== true);
};

export const findUnavailableProductsWithoutAlco = (unavailableItems: BasketLineItem[]) => {
  return unavailableItems.filter(
    (product) =>
      product?.productDetails.unavailableReason?.code !==
        ProductUnavailableReasonCodes.ALCOHOL_LESS_THAN_REQUIRED_QUANTITY &&
      product?.productDetails.unavailableReason?.code !== ProductUnavailableReasonCodes.ALCOHOL_PROHIBITED_PERIOD &&
      product?.productDetails.unavailableReason?.code !== ProductUnavailableReasonCodes.EXPIRED_ALCOHOL_LICENSE
  );
};

export const findUnavailableAlcoProducts = (unavailableItems: BasketLineItem[]) => {
  return unavailableItems.filter(
    (product) =>
      product?.productDetails?.isAlcohol &&
      (product?.productDetails.unavailableReason?.code ===
        ProductUnavailableReasonCodes.ALCOHOL_LESS_THAN_REQUIRED_QUANTITY ||
        product?.productDetails.unavailableReason?.code === ProductUnavailableReasonCodes.ALCOHOL_PROHIBITED_PERIOD ||
        product?.productDetails.unavailableReason?.code === ProductUnavailableReasonCodes.EXPIRED_ALCOHOL_LICENSE)
  );
};

export const getAlcoholProducts = (availableItems: BasketLineItem[]) => {
  if (!availableItems.length) return [];
  return availableItems.filter((product) => Boolean(product?.productDetails?.isAlcohol) === true);
};

export const findBasketConsignmentItemsWithoutPreOrder = (lineItems: BasketLineItem[]): BasketLineItem[] => {
  return lineItems
    .filter(
      (i) =>
        !i.productDetails?.isPreOrder &&
        (i.productDetails?.sellerDetails?.marketplaceType === null ||
          i.productDetails?.sellerDetails?.marketplaceType === MarketplaceShopType.CONSIGNMENT)
    )
    .sort(function (a, b) {
      return a.productDetails?.title.localeCompare(b.productDetails?.title);
    });
};

export const getPreOrderProducts = (availableItems: BasketLineItem[]) => {
  if (!availableItems.length) return [];
  return availableItems.filter((product) => Boolean(product?.productDetails?.isPreOrder) === true);
};

export const getBulkyProducts = (availableItems: BasketLineItem[], isPreOrderEnabled?: boolean) => {
  if (!availableItems.length) return [];
  return availableItems.filter((product) => {
    if (isPreOrderEnabled) {
      return (
        product?.productDetails?.isBulky === true &&
        Boolean(product?.productDetails?.isPreOrder) === false &&
        Boolean(product?.productDetails?.is1PX) === false
      );
    } else {
      return product?.productDetails?.isBulky === true && Boolean(product?.productDetails?.is1PX) === false;
    }
  });
};

export const getShopNamesFromBasketItems = (lineItems: BasketLineItem[]): ISellerFromBasketItems[] => {
  const map = new Map();
  lineItems.forEach((item) => {
    const id = item.productDetails?.sellerDetails?.shopId;
    if (!map.has(id)) {
      map.set(id, {
        name: item.productDetails?.sellerDetails?.shopName,
        id
      });
    }
  });
  const shopNames = Array.from(map.values());
  return shopNames.sort(function (a, b) {
    return a.name.localeCompare(b.name);
  });
};

export const findBasketDropshipItemsByShopName = (lineItems: BasketLineItem[], shopName: string): BasketLineItem[] => {
  return lineItems
    .filter(
      (i) =>
        (i.productDetails?.sellerDetails?.marketplaceType === MarketplaceShopType.DROPSHIP_FBM ||
          i.productDetails?.sellerDetails?.marketplaceType === MarketplaceShopType.DROPSHIP_SOD) &&
        i.productDetails?.sellerDetails?.shopName?.includes(shopName)
    )
    .sort(function (a, b) {
      return a.productDetails?.title.localeCompare(b.productDetails?.title);
    });
};

export const findBasketConsignmentItems = (
  lineItems: BasketLineItem[],
  isPreOrderEnabled?: boolean
): BasketLineItem[] => {
  const filteredLineItems = lineItems.filter(
    (i) =>
      !i.productDetails?.isBulky &&
      !i.productDetails?.is1PX &&
      (isPreOrderEnabled ? !i.productDetails?.isPreOrder : true) &&
      !i.productDetails?.isAlcohol &&
      (i.productDetails?.sellerDetails?.marketplaceType === null ||
        i.productDetails?.sellerDetails?.marketplaceType === MarketplaceShopType.CONSIGNMENT)
  );

  return filteredLineItems.sort(function (a, b) {
    return a.productDetails?.title.localeCompare(b.productDetails?.title);
  });
};

const mapBasketItem = (basketItem: BasketItem): BasketLineItem => {
  const variant = (basketItem?.variants?.length && basketItem?.variants[0]) as Variant | undefined;
  const quantity = basketItem?.quantitySetByUser || 1;

  return {
    quantity,
    productDetails: {
      ...basketItem,
      status: basketItem ? basketItem?.status : ProductStatusType.ARCHIVED
    },
    variantId: variant?.id || '',
    price: (variant?.price || 0) * quantity,
    originPrice: (basketItem?.originPrice || 0) * quantity,
    voucherPrice: 0,
    productId: basketItem?.id
  };
};
export const basketItemsTotal = (lineItems: BasketLineItem[]): number => {
  return lineItems.reduce((sum: number, item: BasketLineItem) => sum + Number(item?.originPrice || 0), 0);
};

export const basketItemsDiscount = (lineItems: BasketLineItem[]): number => {
  return lineItems.reduce(
    (sum: number, item: BasketLineItem) =>
      sum +
      (Number(item?.productDetails?.originPrice || 0) - Number(item?.productDetails?.displayPrice || 0)) *
        item.quantity,
    0
  );
};
export const filterAvailableProducts = (lineItems: BasketLineItem[], storeCodes: string[]): BasketLineItem[] => {
  return lineItems.filter((i) => getProductAvailability(i.productDetails, storeCodes).isAvailable);
};
export const getBasketCalculationData = (
  items: BasketLineItem[],
  basket: BasketState | Basket,
  storeCodes: string[]
) => {
  const subtotal = basketItemsTotal(filterAvailableProducts(items, storeCodes));
  const discount = basketItemsDiscount(filterAvailableProducts(items, storeCodes));
  const shippingFee = basket?.shippingFee;
  const shippingRule = basket?.shippingRule;
  const minimumSpend = basket?.minimumSpend || 0;
  const total = subtotal - discount;
  const priceUnit = items[0]?.productDetails?.priceUnit || '';

  return {
    subtotal,
    discount,
    shippingFee,
    shippingRule,
    minimumSpend,
    total,
    priceUnit
  };
};

export const mapBasketData = (basket: Basket, storeCodes: string[]): BasketRoot => {
  const basketItems = (basket.basketItems || []).map(mapBasketItem).filter((i) => i.variantId);
  const unavailableItems = (basket.unavailableItems || []).map(mapBasketItem);

  return {
    basket: {
      ...basket,
      basketItems,
      unavailableItems,
      ...getBasketCalculationData(basketItems, basket, storeCodes)
    }
  };
};

export function hasItemsInBasket(basket?: BasketState): boolean {
  const basketItemLength = basket?.basketItems?.length ?? 0;
  const unavailableBasketItemLength = basket?.unavailableItems?.length ?? 0;
  return basketItemLength + unavailableBasketItemLength > 0;
}

export const showAsPercentGP = (azureRole: string | undefined): boolean => {
  if (azureRole === AsAzureRoles.AS_SALES_POC || azureRole === AsAzureRoles.AS_LPPOC) {
    return false;
  }
  return true;
};

export const getAlcoholVolumeInLiters = (basketItems: BasketLineItem[]) => {
  if (!basketItems.length) {
    return 0;
  }
  const totalVolumeInMl = basketItems
    ?.filter(({productDetails}) => productDetails.isAlcohol)
    .reduce((total, item) => {
      const volume = Number(item.productDetails?.metafields?.find((field) => field.key === 'volume')?.value ?? '0');
      return total + volume * item.quantity;
    }, 0);
  return totalVolumeInMl / 1000;
};

// round off while "Add all items to basket" to nearest digit
export const roundOffDecimalQuantity = (decimalQty: number) => {
  // convert to number
  const quantity = Number(decimalQty);

  if (Number.isNaN(quantity)) {
    return decimalQty;
  }

  if (!Number.isInteger(quantity)) {
    // return quantity < 1 ? 1 : Math.round(quantity);
    return quantity < 1 ? 1 : quantity;
  }

  return quantity;
};

export const createCheckoutPayload = (
  apiVersion: string,
  storeCode?: string,
  zipCode?: string,
  subDistrict?: string,
  shippingAddress?: Address,
  locale?: string,
  isGoingToSAC?: boolean,
  allowCustomAttributes?: boolean,
  basketSize?: number,
  basketValue?: number,
  accessTradeClickId?: string,
  pricezaClickId?: string,
  isExpress?: boolean,
  appVersion?: string,
  deviceOS?: string,
  makroMemberId?: string,
  trueId?: string
) => {
  const lang = apiVersion === 'direct' ? undefined : locale;
  let customAttributes = [
    {
      key: CustomAttributeKey.SOURCE,
      value: ReactNative.isReactNativeWebView() ? CustomAttributeSource.APP : CustomAttributeSource.WEB
    },
    {
      key: CustomAttributeKey.LOCALE,
      value: locale
    },
    {
      key: CustomAttributeKey.API_VERSION,
      value: apiVersion
    },
    {
      key: CustomAttributeKey.FE_TYPE,
      value: isGoingToSAC ? 'SAC' : 'MKW'
    },
    {
      key: CustomAttributeKey.BASKET_SIZE,
      value: `${basketSize}`
    },
    {
      key: CustomAttributeKey.BASKET_VALUE,
      value: `${basketValue}`
    }
  ];

  if (ReactNative.isReactNativeWebView()) {
    customAttributes = [
      ...customAttributes,
      ...[
        {
          key: CustomAttributeKey.APP_VERSION,
          value: appVersion
        },
        {
          key: CustomAttributeKey.DEVICE_OS,
          value: deviceOS
        },
        {
          key: CustomAttributeKey.USER_ZIP_CODE,
          value: zipCode
        }
      ]
    ];
    if (makroMemberId) {
      customAttributes.push({
        key: CustomAttributeKey.MAKRO_MEMBER_ID,
        value: makroMemberId
      });
    }
  }
  const checkoutPayload: any = {
    storeCode,
    zipCode,
    subDistrict,
    shippingAddress: shippingAddress,
    lang,
    type: isExpress ? CheckoutTypeEnum.EXPRESS : CheckoutTypeEnum.REGULAR,
    ...(allowCustomAttributes && {
      customAttributes
    })
  };

  if (accessTradeClickId) {
    checkoutPayload.clickId = accessTradeClickId;
    checkoutPayload.clickIdSource = AffiliateProgramType.ACCESSTRADE;
  }
  if (pricezaClickId) {
    checkoutPayload.clickId = pricezaClickId;
    checkoutPayload.clickIdSource = AffiliateProgramType.PRICEZA;
  }
  if (trueId) {
    checkoutPayload.clickId = trueId;
    checkoutPayload.clickIdSource = AffiliateProgramType.TRUEID;
  }
  return checkoutPayload;
};

export const isVariantNumeric = (variantId: string) => /^[\d]+$/.test(variantId);

export const parseVariantId = (variantId: string) => {
  return isVariantNumeric(variantId)
    ? variantId
    : getVariantIdWithoutPrefix(Buffer.from(variantId, 'base64').toString());
};

export const findBasketItemByVariantId = (
  lineItems: BasketLineItem[],
  variantId: string
): BasketLineItem | undefined => {
  return lineItems?.find((i: BasketLineItem) => {
    return parseVariantId(i?.variantId || '') === parseVariantId(variantId || '');
  });
};

export const getVariantIdWithoutPrefix = (id: string): string => {
  return id?.includes(VARIANT_ID_PREFIX) ? id?.split(VARIANT_ID_PREFIX).pop() || '' : id;
};

export const getItemDetailsForPromotions = (
  basketItems?: BasketLineItem[],
  basketItemsFromFe?: string[],
  isFlexibleBasket?: boolean
) => {
  if (isFlexibleBasket && !basketItemsFromFe?.length) return [];

  return (
    basketItems?.reduce(
      (selectedItems, item) => {
        if (!isFlexibleBasket || basketItemsFromFe?.includes(item.productDetails.id)) {
          const originPrice = item.productDetails.originPrice;
          const orderedQuantity = item.productDetails.quantitySetByUser ?? 0;

          const is3P =
            item?.productDetails?.variants?.[0]?.metafields?.some(
              (field) => field.key === PROMOTION_LITE_IS_DROPSHIPMENT && field.value === 'true'
            ) ?? false;

          const promotedPrice = item.productDetails.promotedPrice ?? originPrice;
          let unitDiscountAmt = originPrice - promotedPrice;
          const shopifyAttributes = Array.isArray(item.productDetails.customAttributesForShopifyCheckoutFE)
            ? item.productDetails.customAttributesForShopifyCheckoutFE
            : [];
          const customUnitDiscount = shopifyAttributes.find((attr: any) => attr.key === PROMOTION_LITE_UNIT_DISCOUNT);
          if (customUnitDiscount && customUnitDiscount.value !== undefined) {
            unitDiscountAmt = Number(customUnitDiscount.value);
          }
          selectedItems?.push({
            is3P,
            orderedQuantity,
            unitRetail: originPrice,
            netUnitRetails: originPrice,
            unitDiscountAmt: unitDiscountAmt,
            itemCode: item.productDetails.sku
          });
        }
        return selectedItems;
      },
      [] as TGetBasketPromtionsLitePayload['itemDetails']
    ) ?? []
  );
};

export const getPreviewItemsWithBasketItems = (basketItems: BasketLineItem[]) => {
  return basketItems.map((item) => ({
    id: item.productId,
    quantitySetByUser: item.quantity
  }));
};

export const findBasketItemByVariantOrProductId = (
  lineItems: BasketLineItem[],
  variantId?: string,
  productId?: string
): BasketLineItem | undefined => {
  return lineItems?.find((i: BasketLineItem) => {
    return (
      (variantId && parseVariantId(i?.variantId || '') === parseVariantId(variantId)) ||
      (productId && getProductIdWithoutPrefix(i?.productDetails?.id || '') === getProductIdWithoutPrefix(productId))
    );
  });
};

export const findBasketItem = (
  product: Product | ProductSearchResult,
  lineItems?: BasketLineItem[]
): BasketLineItem | undefined => {
  const searchProduct = product as ProductSearchResult;
  const regularProduct = product as Product;

  const variantId = regularProduct?.variants?.length ? regularProduct?.variants[0]?.id : searchProduct?.variantId;

  return product && findBasketItemByVariantOrProductId(lineItems || [], variantId, product?.id);
};
export const removeAlreadySelectedItems = (newItems: string[], selectedItems: string[]) =>
  newItems.filter((itm) => !selectedItems?.includes(itm));

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const fetchLatestBasketItem = (basketItems: BasketLineItem[]): BasketLineItem | null => {
  if (!basketItems?.length || !basketItems[0].productDetails?.dateCreated) {
    return null;
  }

  return basketItems.reduce((latest, current) => {
    const latestDate = new Date(latest.productDetails?.dateCreated).getTime();
    const currentDate = new Date(current.productDetails?.dateCreated).getTime();
    return currentDate > latestDate ? current : latest;
  });
};
